import React, { useState, useRef, useEffect } from 'react';
import { LogOut, ChevronDown, ClipboardList, Trophy, MessageSquare, Zap, RefreshCw, Users, TrendingUp, Briefcase, CheckCircle, ShieldAlert, Bell, BellOff } from 'lucide-react';
import ThemeToggle from '../ui/ThemeToggle';

interface NavbarProps {
  userName: string;
  userEmail: string;
  userPhoto: string | null;
  onLogout: () => void;
  onNavigate?: (page: string) => void;
  // Orchestration
  onRunOrchestration?: () => Promise<void>;
  isOrchestrating?: boolean;
  // Price refresh
  onRefreshPositions?: () => Promise<void>;
  isRefreshing?: boolean;
  // Stats
  agentCount?: number;
  avgWinRate?: number;
  openPositionCount?: number;
  closedPositionCount?: number;
  isAdmin?: boolean;
  currentPage?: string;
  newsletterEnabled?: boolean;
  onToggleNewsletter?: (enabled: boolean) => Promise<void>;
}

// Quantify Logo Component - uses logo.png from public folder
const QuantifyLogo = () => (
  <img 
    src="/logo.png" 
    alt="Quantify" 
    className="w-10 h-10 object-cover"
    style={{ transform: 'scale(1.3)' }}
  />
);

const Navbar: React.FC<NavbarProps> = ({
  userName,
  userEmail,
  userPhoto,
  onLogout,
  onNavigate,
  onRunOrchestration,
  isOrchestrating = false,
  onRefreshPositions,
  isRefreshing = false,
  agentCount = 0,
  avgWinRate = 0,
  openPositionCount = 0,
  closedPositionCount = 0,
  isAdmin = false,
  currentPage = 'dashboard',
  newsletterEnabled = true,
  onToggleNewsletter,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [cooldownSecs, setCooldownSecs] = useState(() => {
    const stored = localStorage.getItem('orchestration_cooldown_until');
    if (stored) {
      const remaining = Math.floor((parseInt(stored) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });
  const [adminToast, setAdminToast] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [refreshCooldownSecs, setRefreshCooldownSecs] = useState(() => {
    const stored = localStorage.getItem('refresh_cooldown_until');
    if (stored) {
      const remaining = Math.floor((parseInt(stored) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });
  const [refreshTriggered, setRefreshTriggered] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cooldown timers
  useEffect(() => {
    if (cooldownSecs <= 0 && refreshCooldownSecs <= 0) return;
    const t = setInterval(() => {
      setCooldownSecs(s => Math.max(0, s - 1));
      setRefreshCooldownSecs(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownSecs, refreshCooldownSecs]);

  const handleRunCycle = async () => {
    if (!isAdmin) {
      setAdminToast(true);
      setTimeout(() => setAdminToast(false), 3000);
      return;
    }
    if (cooldownSecs > 0 || isOrchestrating || !onRunOrchestration) return;
    await onRunOrchestration();
    setTriggered(true);
    setTimeout(() => setTriggered(false), 3000);
    setCooldownSecs(900);
    localStorage.setItem('orchestration_cooldown_until', String(Date.now() + 900000));
  };

  const handleRefresh = async () => {
    if (!isAdmin) {
      setAdminToast(true);
      setTimeout(() => setAdminToast(false), 3000);
      return;
    }
    if (refreshCooldownSecs > 0 || isRefreshing || !onRefreshPositions) return;
    await onRefreshPositions();
    setRefreshTriggered(true);
    setTimeout(() => setRefreshTriggered(false), 3000);
    setRefreshCooldownSecs(900);
    localStorage.setItem('refresh_cooldown_until', String(Date.now() + 900000));
  };

  return (
    <header className="bg-gradient-to-r from-slate-900 via-slate-800/95 to-slate-900 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-50">
      <div className="px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left - Logo and Brand */}
          <div
            className="flex items-center gap-2 cursor-pointer min-w-0 shrink"
            onClick={() => onNavigate?.('dashboard')}
          >
            <QuantifyLogo />
            <div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-green-400 via-cyan-400 to-emerald-500 bg-clip-text text-transparent">
                Quantify
              </h1>
            </div>
          </div>

          {/* Center - Live Stats.
              7.3: hidden below md — four chips + logo + actions is ~600px of content in a
              390px viewport. No information is lost: agent count / win rate / open / closed
              all render on the Dashboard, which is the mobile default page. */}
          <div className="hidden md:flex items-center gap-2">
            <div
              onClick={() => onNavigate?.('agents')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800/60 rounded-lg border border-slate-700/30 cursor-pointer hover:bg-slate-700/60 hover:border-cyan-500/30 transition-colors"
            >
              <Users className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs font-bold text-white tabular-nums">{agentCount}</span>
              <span className="text-10 text-slate-400">agents</span>
            </div>
            <div
              onClick={() => onNavigate?.('all-orders:open')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800/60 rounded-lg border border-slate-700/30 cursor-pointer hover:bg-slate-700/60 hover:border-amber-500/30 transition-colors"
            >
              <Briefcase className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-bold text-white tabular-nums">{openPositionCount}</span>
              <span className="text-10 text-slate-400">open</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800/60 rounded-lg border border-slate-700/30">
              <TrendingUp className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-xs font-bold text-white tabular-nums">{avgWinRate.toFixed(0)}%</span>
              <span className="text-10 text-slate-400">win rate</span>
            </div>
            <div
              onClick={() => onNavigate?.('all-orders:closed')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800/60 rounded-lg border border-slate-700/30 cursor-pointer hover:bg-slate-700/60 hover:border-emerald-500/30 transition-colors"
            >
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-bold text-white tabular-nums">{closedPositionCount}</span>
              <span className="text-10 text-slate-400">closed</span>
            </div>
          </div>

          {/* Right - Run Cycle + User Profile */}
          <div className="flex items-center gap-2 shrink-0">
            {currentPage === 'dashboard' && (
              <div className="relative">
                <button
                  onClick={handleRunCycle}
                  disabled={isAdmin && (isOrchestrating || cooldownSecs > 0)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-white text-11 font-semibold rounded-lg transition-all shadow-lg ${
                    isAdmin
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:from-slate-600 disabled:to-slate-600 shadow-cyan-500/10'
                      : 'bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 shadow-none'
                  }`}
                >
                  {isOrchestrating ? <RefreshCw className="w-3 h-3 animate-spin" /> : triggered ? <CheckCircle className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                  <span className="hidden md:inline">{isOrchestrating ? 'Triggering...' : triggered ? 'Cycle Triggered!' : cooldownSecs > 0 ? `${Math.floor(cooldownSecs / 60)}:${String(cooldownSecs % 60).padStart(2, '0')}` : 'Run Cycle'}</span>
                </button>
                {adminToast && (
                  <div className="absolute top-full right-0 mt-2 w-56 px-3 py-2 bg-slate-800 border border-amber-500/30 rounded-lg shadow-xl z-[70] animate-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0" />
                      <p className="text-11 text-slate-300">Only admins can run cycles. Cycles run automatically at <span className="text-amber-400 font-medium">10 AM & 2 PM ET</span>.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            {currentPage === 'dashboard' && (
              <button
                onClick={handleRefresh}
                disabled={isAdmin && (isRefreshing || refreshCooldownSecs > 0)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-white text-11 font-semibold rounded-lg transition-all ${
                  isAdmin
                    ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 disabled:from-slate-600 disabled:to-slate-600 shadow-emerald-500/10 shadow-lg'
                    : 'bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600'
                }`}
                title="Refresh live prices for all positions"
              >
                {isRefreshing ? <RefreshCw className="w-3 h-3 animate-spin" /> : refreshTriggered ? <CheckCircle className="w-3 h-3" /> : <RefreshCw className="w-3 h-3" />}
                <span className="hidden md:inline">{isRefreshing ? 'Refreshing...' : refreshTriggered ? 'Updated!' : refreshCooldownSecs > 0 ? `${Math.floor(refreshCooldownSecs / 60)}:${String(refreshCooldownSecs % 60).padStart(2, '0')}` : 'Refresh'}</span>
              </button>
            )}
            {/* User Profile with Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 transition-colors"
              >
                {userPhoto ? (
                  <img 
                    src={userPhoto} 
                    alt={userName}
                    referrerPolicy="no-referrer"
                    className="w-7 h-7 rounded-full border border-green-500/30 object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`w-7 h-7 rounded-full bg-gradient-to-br from-green-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xs ${userPhoto ? 'hidden' : ''}`}>
                  {userName?.charAt(0) || userEmail?.charAt(0) || '?'}
                </div>
                <div className="hidden sm:block">
                  <p className="text-xs font-medium text-white leading-tight">{userName || 'User'}</p>
                  <p className="text-9 text-slate-400 leading-tight truncate max-w-[100px]">{userEmail}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-xl bg-slate-800 border border-slate-700/50 shadow-xl z-[60] overflow-hidden">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        console.log('🔘 AI Agents clicked! Calling onNavigate("agents")');
                        onNavigate?.('agents');
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-white transition-colors"
                    >
                      <Trophy className="w-4 h-4 text-yellow-400" />
                      AI Agents
                    </button>
                    <button
                      onClick={() => {
                        onNavigate?.('chat');
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-white transition-colors"
                    >
                      <MessageSquare className="w-4 h-4 text-purple-400" />
                      Agent Chat
                    </button>
                    <button
                      onClick={() => {
                        onNavigate?.('all-orders');
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-white transition-colors"
                    >
                      <ClipboardList className="w-4 h-4" />
                      All Orders
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => {
                          onNavigate?.('admin');
                          setIsDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors"
                      >
                        <ShieldAlert className="w-4 h-4 text-red-400" />
                        Admin Panel
                      </button>
                    )}
                    <div className="border-t border-slate-700/50 my-1" />
                    <button
                      onClick={async () => {
                        if (onToggleNewsletter) {
                          await onToggleNewsletter(!newsletterEnabled);
                        }
                      }}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-white transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        {newsletterEnabled ? <Bell className="w-4 h-4 text-emerald-400" /> : <BellOff className="w-4 h-4 text-slate-500" />}
                        Notifications
                      </span>
                      <span className={`text-10 px-1.5 py-0.5 rounded font-bold ${
                        newsletterEnabled
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-slate-700 text-slate-500'
                      }`}>
                        {newsletterEnabled ? 'ON' : 'OFF'}
                      </span>
                    </button>
                    <div className="border-t border-slate-700/50 my-1" />
                    <ThemeToggle />
                    <div className="border-t border-slate-700/50 my-1" />
                    <button
                      onClick={() => {
                        onLogout();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
